import { BulkGetEmployeeGroupsResponse } from '@electricjs/core_entity-client';
import { http, HttpResponse, delay } from 'msw';

import {
  employeeGroups,
  simpleGroupApplicationList,
} from '@/mocks/data/groupData';
import { simpleOrganizationGroups } from '@/mocks/data/organizationData';
import { mockOrganizationEmployees } from '@/mocks/handlers/employeeHandlers';

export const groupHandlers = [
  http.delete(
    '*/v1/organizations/:organizationId/groups/:groupId/employees/:employeeId',
    async ({ params }) => {
      // find the group in the mock data (org-level or an employee's group)
      const group =
        simpleOrganizationGroups.find(group => group.id === params.groupId) ??
        employeeGroups.find(group => group.id === params.groupId);

      // Update employee count if group exists
      if (group && group.employees_count > 0) {
        group.employees_count -= 1;

        await delay();
        return new HttpResponse(null, { status: 200 });
      } else if (group) {
        await delay();
        return new HttpResponse(null, { status: 200 });
      } else {
        await delay();
        return new HttpResponse(null, { status: 404 });
      }
    }
  ),

  http.patch(
    '*/v1/organizations/:organizationId/groups/:groupId',
    async ({ params, request }) => {
      // find the group in the mock data
      const group = simpleOrganizationGroups.find(
        group => group.id === params.groupId
      );

      if (group) {
        // Apply the fields the client actually sent so rename and reparent
        // flows (EditGroupModal) are reflected on refetch. A missing
        // parent_group_id leaves the parent alone, matching the real API.
        const body = (await request.json()) as {
          name?: string;
          parent_group_id?: string | null;
        };
        if (body.name !== undefined) group.name = body.name;
        if (body.parent_group_id !== undefined) {
          group.parent_group_id = body.parent_group_id ?? undefined;
        }
        await delay();
        return new HttpResponse(null, { status: 200 });
      } else {
        await delay();
        return new HttpResponse(null, { status: 404 });
      }
    }
  ),

  // Accepts the per-employee review trigger fired after a group reparent
  // (EditGroupModal → triggerGroupChangeReview). No task is modeled.
  http.post(
    '*/v1/organizations/:organizationId/employees/:employeeId/group-change-review',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),

  // Returns empty so `useGetApplicationsForGroups` can `.unwrap()` without
  // throwing. The combined apps list comes from the `applications` route below
  // (per group, not ancestors), so an empty ancestors response is fine.
  http.get('*/v1/*/groups/:groupId/ancestors_applications', async () => {
    await delay();
    return HttpResponse.json([], { status: 200 });
  }),

  // Assigns an application to a group — used by both AssignApplicationToGroupModal
  // and the Configure Applications page's per-app group selection.
  http.post(
    '*/v1/organizations/:organizationId/groups/:groupId/applications/:applicationKey',
    async ({ params }) => {
      const group =
        simpleOrganizationGroups.find(g => g.id === params.groupId) ??
        employeeGroups.find(g => g.id === params.groupId);

      await delay();
      return new HttpResponse(null, { status: group ? 200 : 404 });
    }
  ),

  // Returns every mock org employee for any known group, same simplification
  // as the rule-builder preview endpoint below — mock data doesn't model real
  // group membership, so this is good enough for exercising the "grant access
  // to a group's employees" flows (AssignApplicationToGroupModal, Configure
  // Applications access-type selection) without a real membership graph.
  http.get(
    '*/v1/organizations/:organizationId/groups/:groupId/employees',
    async ({ params, request }) => {
      const group =
        simpleOrganizationGroups.find(g => g.id === params.groupId) ??
        employeeGroups.find(g => g.id === params.groupId);

      await delay();
      if (!group) {
        return HttpResponse.json({ results: [], total: 0 }, { status: 200 });
      }

      const url = new URL(request.url);
      const limitParam = url.searchParams.get('limit');
      const offsetParam = url.searchParams.get('offset');
      let results = mockOrganizationEmployees;
      const total = results.length;
      if (limitParam) {
        const limit = parseInt(limitParam, 10);
        const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
        results = results.slice(offset, offset + limit);
      }

      return HttpResponse.json({ results, total }, { status: 200 });
    }
  ),

  http.get('*/v1/*/groups/:groupId/applications', async ({ params }) => {
    // Look up the group in any mock group set — `simpleOrganizationGroups`
    // (org-level) or `employeeGroups` (returned by the per-employee groups
    // endpoint). Falling back to an empty app list (instead of 404) keeps the
    // offboarding flow's `useGetApplicationsForGroups` happy for groups we
    // haven't authored apps for.
    const group =
      simpleOrganizationGroups.find(g => g.id === params.groupId) ??
      employeeGroups.find(g => g.id === params.groupId);

    await delay();
    if (!group) {
      return HttpResponse.json([], { status: 200 });
    }
    return HttpResponse.json(simpleGroupApplicationList, { status: 200 });
  }),

  // Dynamic-group rule-builder live preview. Returns every mock org employee
  // as a "match" rather than actually evaluating `rule_groups` — good enough
  // for exercising the LivePreviewPanel wiring (including its infinite-scroll
  // pagination) without a rule engine. Deliberately draws from the SAME
  // `mockOrganizationEmployees` pool as the org employee-count endpoint
  // (rather than a separate synthetic list), so "N matches" can never exceed
  // the org's actual employee count — that combination isn't a real state.
  // Honors both `limit` and `offset` so paging through locally actually
  // advances through the dataset instead of repeating the first page.
  http.post(
    '*/v1/organizations/:organizationId/groups/preview/employees',
    async ({ request }) => {
      const url = new URL(request.url);
      const offset = Number(url.searchParams.get('offset')) || 0;
      const limitParam = url.searchParams.get('limit');
      const limit = limitParam !== null ? Number(limitParam) : undefined;

      const results = mockOrganizationEmployees.slice(
        offset,
        limit !== undefined ? offset + limit : undefined
      );
      await delay();
      return HttpResponse.json(
        { results, total: mockOrganizationEmployees.length },
        { status: 200 }
      );
    }
  ),

  // Adds the given org employees to the group and returns them. Membership
  // isn't modeled (see the GET employees handler above), only the count.
  http.post(
    '*/v1/organizations/:organizationId/groups/:groupId/employees',
    async ({ params, request }) => {
      const group =
        simpleOrganizationGroups.find(g => g.id === params.groupId) ??
        employeeGroups.find(g => g.id === params.groupId);
      const body = (await request.json()) as { employee_ids: string[] };
      const employees = mockOrganizationEmployees.filter(employee =>
        body.employee_ids.includes(employee.id)
      );

      await delay();
      if (!group) {
        return new HttpResponse(null, { status: 404 });
      }
      group.employees_count += employees.length;
      return HttpResponse.json(employees, { status: 200 });
    }
  ),

  http.delete(
    '*/v1/organizations/:organizationId/groups/:groupId/applications/:applicationKey',
    async ({ params }) => {
      const group =
        simpleOrganizationGroups.find(g => g.id === params.groupId) ??
        employeeGroups.find(g => g.id === params.groupId);

      await delay();
      return new HttpResponse(null, { status: group ? 200 : 404 });
    }
  ),

  // Every requested employee gets the same mock group list; membership isn't modeled.
  http.post(
    '*/v1/organizations/:organizationId/employees/groups/bulk',
    async ({ request }) => {
      const body = (await request.json()) as { employee_ids: string[] };
      const response: BulkGetEmployeeGroupsResponse = {
        employee_groups: Object.fromEntries(
          body.employee_ids.map(employeeId => [employeeId, employeeGroups])
        ),
      };

      await delay();
      return HttpResponse.json(response, { status: 200 });
    }
  ),

  // Demo variant of the group-change-review trigger above. No task is modeled.
  http.post(
    '*/v1/organizations/:organizationId/employees/:employeeId/demo-group-change-review',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
];
